import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, TriangleAlert } from "lucide-react";

import { AddCapitalSourceDialog } from "../../../components/portfolio-risk-concentration/AddCapitalSourceDialog";
import { ContractValuationSettings } from "../../../components/portfolio-risk-concentration/ContractValuationSettings";
import { CountBadge } from "../../../components/portfolio-risk-concentration/CountBadge";
import { DiscountSpreadSettingsDialog } from "../../../components/portfolio-risk-concentration/DiscountSpreadSettingsDialog";
import { EligibilityConditionDialog } from "../../../components/portfolio-risk-concentration/EligibilityConditionDialog";
import { ServerSortHead, type SortDir } from "../../../components/portfolio-risk-concentration/ServerSortHead";
import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import { SegmentHeader } from "../../../components/portfolio-risk-concentration/SegmentHeader";
import { ManageLimitsShell } from "../../../components/portfolio-risk-concentration/StepShell";
import { ZeroOutRuleDialog } from "../../../components/portfolio-risk-concentration/ZeroOutRuleDialog";
import { ratePct, usd } from "../../../components/portfolio-risk-concentration/format";
import { INVESTOR_LABEL } from "../../../components/portfolio-risk-concentration/PrcFilterBar";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader } from "../../../ui/card";
import { Dialog } from "../../../ui/dialog";
import { PrcDialogContent } from "../../../components/portfolio-risk-concentration/PrcDialogContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { cn } from "../../../utils/common/cn";
import {
  CHANGE_KIND_LABEL,
  limitsDeepLink,
  useCapitalProposals,
  useCapitalSources,
  useInvestors,
  useProposeCapital,
  type CapitalChangeKind,
  type CapitalReviewResult,
  type CapitalSource,
} from "../../../utils/portfolio-risk-concentration/api/capital";
import {
  dcbRateSummary,
  fmvSummaryLabel,
  useDiscountSpreadSettings,
  type DiscountSortKey,
  type DiscountSpreadSetting,
} from "../../../utils/portfolio-risk-concentration/api/discountSettings";
import {
  eligibilitySelectionSummary,
  useEligibilityConditions,
  type EligibilityCondition,
  type EligibilitySortKey,
} from "../../../utils/portfolio-risk-concentration/api/eligibility";
import {
  usePendingFairValueSpreadBatches,
} from "../../../utils/portfolio-risk-concentration/api/fairValueSpread";
import {
  useZeroOutRules,
  type ZeroOutRule,
  type ZeroOutSortKey,
} from "../../../utils/portfolio-risk-concentration/api/zeroOutRules";

/**
 * Manage Capital — creating and managing the capital every limit is measured against.
 *
 * ── Why this is a sub-step of Monitor and not a page of its own ─────────────────────────
 * It sits under Monitor's + toggle beside Manage limits and Manage alerts, and FIRST among
 * them: registering capital always has to happen before limits can be set against it, so
 * the breadcrumb reads in the order the dependency actually runs.
 *
 * ── What makes a ceiling worth governing ────────────────────────────────────────────────
 * A limit says "35% of capital". Capital is the other half of that sentence, and moving it
 * moves every percentage, headroom and RAG status underneath without a limit having been
 * touched. So a ceiling change is proposed, approved with mandatory notes, effective-dated
 * and logged — the same mechanics as a limit change, not a lighter version.
 *
 * ── Three facts about a source, and the log says which one changed ──────────────────────
 * A ceiling is one of three definitional properties, and all three are governed the same
 * way: the committed CEILING, the ADVANCE RATE a lender applies to pledged collateral, and
 * the CODE MAPPING that says which raw BankDwn investor codes constitute the pool. They are
 * all "what this source IS" — which is what puts them here rather than in Manage limits,
 * whose job is what is allowed INSIDE a source.
 *
 * Each gets its OWN proposal, approval and log row. A ceiling rise and a code reassignment
 * are different decisions — one moves the denominator under every limit, the other changes
 * which contracts are in scope at all — so a reviewer sees them apart and the log says which
 * kind each entry was. The per-source lock keys on (source, kind) for the same reason: a
 * pending mapping change must not block a ceiling adjustment.
 *
 * ── The auto-trigger ────────────────────────────────────────────────────────────────────
 * A capital source with no limits is unconstrained: nothing is stopping deployment
 * concentrating however it happens to land. On approving a CREATION, prc-api answers with
 * `needsLimits`, and the prompt below offers a deep link into Manage limits pre-scoped to
 * that source. Dismissing it does not lose the fact — the row keeps a "No limits set"
 * indicator, which opens the same link, until its first limit exists.
 *
 * It deliberately does not fire on ceiling ADJUSTMENTS to a source that already has limits.
 * Percentage limits recompute against the new base on their own, so there is no setup action
 * to prompt for, and a prompt on every routine change is one people learn to dismiss.
 */

// Segments 2-4 now fetch one real server-side page at a time (limit/offset sent to the
// backend) -- same "X-Y of Z" + Prev/Next footer ContractValuationSettings.tsx's FairValueLog
// established, now backed by an actual page fetch instead of a client-side slice.
const SEGMENT_PAGE_SIZE = 10;

/** Sources sort live-first, then investor, then equity before books. */
function sortSources(rows: CapitalSource[]): CapitalSource[] {
  const rank = (s: CapitalSource) =>
    `${s.status === "LIVE" ? 0 : 1}${s.investor}${s.capitalType === "Equity" ? 0 : 1}${
      s.facilityCode ?? ""
    }`;
  return [...rows].sort((a, b) => rank(a).localeCompare(rank(b)));
}

type CapitalSortKey =
  | "investor"
  | "facility"
  | "type"
  | "ceiling"
  | "advanceRate"
  | "mappedCodes"
  | "effectiveFrom"
  | "status";

const TEXT_SORTS: CapitalSortKey[] = ["investor", "facility", "type", "effectiveFrom"];

/**
 * Capital Sources sorted by the column a header names (the table's own order when none is). A blank value -- no advance rate recorded,
 * no mapping set -- sorts last whichever way the column runs, so the rows that need attention do not jump to the top when the direction
 * flips. Status ascending is Live first; Mapped Codes sorts by how many codes are mapped.
 */
function sortFundingSources(
  rows: CapitalSource[],
  key: CapitalSortKey | null,
  dir: SortDir,
  investorLabel: (code: string) => string,
): CapitalSource[] {
  if (key === null) return rows;
  const sign = dir === "asc" ? 1 : -1;
  const text = (s: CapitalSource): string | null => {
    switch (key) {
      case "investor":
        return investorLabel(s.investor);
      case "facility":
        return s.facilityName ?? s.facilityCode ?? null;
      case "type":
        return s.capitalType;
      case "effectiveFrom":
        return s.effectiveStartDate;
      default:
        return null;
    }
  };
  const num = (s: CapitalSource): number | null => {
    switch (key) {
      case "ceiling":
        return s.ceilingDollars;
      case "advanceRate":
        return s.advanceRatePct;
      case "mappedCodes":
        return s.investorCodes === null ? null : s.investorCodes.length;
      case "status":
        return s.status === "LIVE" ? 0 : 1;
      default:
        return null;
    }
  };
  const byText = TEXT_SORTS.includes(key);
  return [...rows].sort((a, b) => {
    const x = byText ? text(a) : num(a);
    const y = byText ? text(b) : num(b);
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    const c = typeof x === "string" && typeof y === "string" ? x.localeCompare(y) : Number(x) - Number(y);
    return c * sign;
  });
}

export default function ManageCapitalPage() {
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get("from") ?? undefined;

  // Segment 1 has no Prev/Next of its own -- this table has always been a handful of rows
  // ("four rows long today" per capital.py's own docstring) -- so it fetches a large page to
  // keep showing everything, same {limit:100} idiom as a small governance queue/log elsewhere.
  const sourcesQ = useCapitalSources(false, { limit: 100, offset: 0 });
  const proposalsQ = useCapitalProposals(undefined, { limit: 100, offset: 0 });
  // For display names only. A source registered under a brand-new investor would otherwise
  // render its raw code — "JAGUAR_TRUST" — because INVESTOR_LABEL is a fixed map of the two
  // investors that predate "+ Add new investor".
  const investorsQ = useInvestors();
  const propose = useProposeCapital();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSource, setEditSource] = useState<CapitalSource | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  /** The post-approval prompt. Null when nothing is being offered. */
  const [prompt, setPrompt] = useState<CapitalReviewResult | null>(null);

  // Segments 2-4: direct-write settings, no proposal queues of their own — see each API
  // module's docstring for why (the tables have no governance trail).
  const [eligibilityOffset, setEligibilityOffset] = useState(0);
  // The table sorts across every code's conditions (the backend does it), so a new sort starts again from the first page.
  const [eligibilitySort, setEligibilitySort] = useState<EligibilitySortKey | null>(null);
  const [eligibilityDir, setEligibilityDir] = useState<SortDir>("asc");
  const sortEligibility = (key: EligibilitySortKey | null, dir: SortDir) => {
    setEligibilitySort(key);
    setEligibilityDir(dir);
    setEligibilityOffset(0);
  };
  const eligibilityQ = useEligibilityConditions({
    limit: SEGMENT_PAGE_SIZE,
    offset: eligibilityOffset,
    sort: eligibilitySort,
    dir: eligibilityDir,
  });
  const [eligibilityDialogOpen, setEligibilityDialogOpen] = useState(false);
  const [editEligibility, setEditEligibility] = useState<EligibilityCondition | null>(null);

  const [discountOffset, setDiscountOffset] = useState(0);
  const [discountSort, setDiscountSort] = useState<DiscountSortKey | null>(null);
  const [discountDir, setDiscountDir] = useState<SortDir>("asc");
  const sortDiscount = (key: DiscountSortKey | null, dir: SortDir) => {
    setDiscountSort(key);
    setDiscountDir(dir);
    setDiscountOffset(0);
  };
  const discountSettingsQ = useDiscountSpreadSettings({
    limit: SEGMENT_PAGE_SIZE,
    offset: discountOffset,
    sort: discountSort,
    dir: discountDir,
  });
  // The dialog's own "Configure" picker lets a user choose ANY setting to edit, not just one
  // already on screen -- it needs every row, independent of the table's own page.
  const allDiscountSettingsQ = useDiscountSpreadSettings({ limit: 100, offset: 0 });
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [editDiscountSetting, setEditDiscountSetting] = useState<DiscountSpreadSetting | null>(
    null,
  );
  const [matrixDialogOpen, setMatrixDialogOpen] = useState(false);
  // Same pending count the Spread Matrix popup's own approval queue shows — this resolves to
  // the identical React Query key, so it's one shared cache entry / one network request, not
  // a second fetch.
  const pendingFvBatchesQ = usePendingFairValueSpreadBatches();
  const pendingFvBatchCount = pendingFvBatchesQ.data?.batches.length ?? 0;

  const [zeroOutOffset, setZeroOutOffset] = useState(0);
  const [zeroOutSort, setZeroOutSort] = useState<ZeroOutSortKey | null>(null);
  const [zeroOutDir, setZeroOutDir] = useState<SortDir>("asc");
  const sortZeroOut = (key: ZeroOutSortKey | null, dir: SortDir) => {
    setZeroOutSort(key);
    setZeroOutDir(dir);
    setZeroOutOffset(0);
  };
  const zeroOutQ = useZeroOutRules({
    limit: SEGMENT_PAGE_SIZE,
    offset: zeroOutOffset,
    sort: zeroOutSort,
    dir: zeroOutDir,
  });
  const [zeroOutDialogOpen, setZeroOutDialogOpen] = useState(false);
  const [editZeroOutRule, setEditZeroOutRule] = useState<ZeroOutRule | null>(null);

  const sources = useMemo(() => sortSources(sourcesQ.data?.sources ?? []), [sourcesQ.data]);
  // The landing table lists FUNDING only — where the money to write business comes from.
  // Internal caps (appetite ceilings, not contributed capital) used to have their own block
  // below this table; that block is gone, replaced by Contract Valuation Settings. Excluded
  // here for the same reason as before — mixing them into the funding table made "Navitas
  // $385M" appear twice, once as their equity and once as the cap over it.
  const fundingSources = useMemo(
    () => sources.filter((s) => s.capitalType !== "Internal"),
    [sources],
  );
  const proposals = proposalsQ.data?.proposals ?? [];
  const pending = proposals.filter((p) => p.status === "PENDING");

  // Segments 2-4: real server-side pages now — xPage/xTotal are direct reads off the query
  // result rather than a client-side slice of the whole table.
  const eligibilityPage = eligibilityQ.data?.eligibility ?? [];
  const eligibilityTotal = eligibilityQ.data?.total ?? 0;

  const discountPage = discountSettingsQ.data?.settings ?? [];
  const discountTotal = discountSettingsQ.data?.total ?? 0;

  const zeroOutPage = zeroOutQ.data?.rules ?? [];
  const zeroOutTotal = zeroOutQ.data?.total ?? 0;

  /** Display name for an investor, from the registry, falling back to the raw code. */
  const investorLabel = useMemo(() => {
    const byCode = new Map((investorsQ.data ?? []).map((i) => [i.investorCode, i.displayName]));
    return (code: string) =>
      byCode.get(code) ?? INVESTOR_LABEL[code as "WAFRA"] ?? code;
  }, [investorsQ.data]);

  // Capital Sources holds every row in the browser (a handful), so it sorts here; its own order is sortSources' until a header is used.
  const [sourceSort, setSourceSort] = useState<CapitalSortKey | null>(null);
  const [sourceDir, setSourceDir] = useState<SortDir>("asc");
  const sortSourceTable = (key: CapitalSortKey | null, dir: SortDir) => {
    setSourceSort(key);
    setSourceDir(dir);
  };
  const shownSources = useMemo(
    () => sortFundingSources(fundingSources, sourceSort, sourceDir, investorLabel),
    [fundingSources, sourceSort, sourceDir, investorLabel],
  );

  /**
   * Which FACTS are in flight, per source.
   *
   * Same lock semantics as Manage limits — one in-flight proposal at a time, every other row
   * fully actionable — but keyed on the FACT rather than the source, matching the partial
   * index prc-api enforces it with since sql/83_. A pending code-mapping change and a
   * pending ceiling change on one facility are independent decisions a reviewer may take in
   * either order, so neither blocks the other, and the row names what is pending rather than
   * just reporting that something is.
   */
  const pendingKinds = useMemo(() => {
    const byId = new Map<string, CapitalChangeKind[]>();
    for (const p of pending) {
      if (!p.capitalSourceId) continue;
      byId.set(p.capitalSourceId, [...(byId.get(p.capitalSourceId) ?? []), p.changeKind]);
    }
    return byId;
  }, [pending]);

  // Monitor is the DEFAULT, because this page sits under Monitor in the step navigation
  // and the step nav is the only way in — it passes no `?from=` — so the default is what
  // everyone actually sees, and it has to point at the parent. (It hung off Capacity until
  // Manage Capital moved up beside Manage Limits and Manage Alerts; sending people back to
  // Capacity now would land them somewhere they were never coming from.)
  //
  // `?from=capacity` is still honoured, since Capacity is the page these ceilings show up
  // on and a link from there is the obvious next one to add.
  const back =
    fromParam === "capacity"
      ? { to: "/risk-concentrations/capacity", label: "← Back to Capacity" }
      : { to: "/risk-concentrations", label: "← Back to Monitor" };

  return (
    <ManageLimitsShell backTo={back.to} backLabel={back.label}>
      <PageHeader title="Manage Capital" />

      {toast && (
        <div className="mt-4 rounded-md border border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 px-3 py-2 text-xs">
          {toast}
        </div>
      )}

      {/* The immediate, dismissible prompt. Rendered above the table rather than as a modal:
          it is an offer, not a decision that has to be made before anything else can
          happen, and the row indicator behind it is the durable version. */}
      {prompt && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber/50 bg-amber-dim/40 px-4 py-3">
          <div className="flex items-start gap-2 text-sm">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber" />
            <span>
              <strong>{promptLabel(prompt)}</strong> now exists with no limits configured.
              Nothing is constraining how its capital is deployed. Set up its limits now?
            </span>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" asChild>
              <Link
                to={limitsDeepLink({
                  investor: prompt.investor ?? "WAFRA",
                  capitalType: prompt.capitalType ?? "Debt",
                  facilityCode: prompt.facilityCode,
                })}
              >
                Set Up Limits Now
              </Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPrompt(null)}>
              Later
            </Button>
          </div>
        </div>
      )}

      {/* The Manage Capital card — capital sources only. The approval queue and the change
          log now live inside the Add/Adjust Capital Source dialog itself, the same pattern
          Discount & Spread Settings' "Spread Matrix" popup already uses. */}
      <Card className="mt-3">
        <CardHeader>
          <SegmentHeader
            title="Capital Sources"
            description="Does this pool exist, and how big is it. Investor, facility, type, ceiling, advance rate, and which raw investor codes belong to it — existing, unchanged."
            right={
              <span className="relative inline-flex">
                <Button
                  size="sm"
                  onClick={() => {
                    setEditSource(null);
                    setDialogOpen(true);
                  }}
                >
                  + Add Capital Source
                </Button>
                <CountBadge count={pending.length} />
              </span>
            }
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* Every column but Action sorts: click once for ascending, again for descending, a third time for the
                      table's own order. */}
                  <ServerSortHead label="Investor" sortKey="investor" active={sourceSort} dir={sourceDir} onSort={sortSourceTable} />
                  <ServerSortHead label="Facility" sortKey="facility" active={sourceSort} dir={sourceDir} onSort={sortSourceTable} />
                  <ServerSortHead label="Type" sortKey="type" active={sourceSort} dir={sourceDir} onSort={sortSourceTable} />
                  <ServerSortHead label="Ceiling" sortKey="ceiling" active={sourceSort} dir={sourceDir} onSort={sortSourceTable} className="text-right" />
                  {/* Both new columns sit beside the ceiling because they are the same category
                      of fact — what this source IS. Visible at a glance rather than buried
                      inside each row's edit form, which is the whole point of putting them
                      here: a facility with no advance rate recorded, or a pool mapped to no
                      codes at all, is something you should be able to see without clicking. */}
                  <ServerSortHead label="Advance Rate" sortKey="advanceRate" active={sourceSort} dir={sourceDir} onSort={sortSourceTable} className="text-right" />
                  <ServerSortHead label="Mapped Codes" sortKey="mappedCodes" active={sourceSort} dir={sourceDir} onSort={sortSourceTable} />
                  <ServerSortHead label="Effective From" sortKey="effectiveFrom" active={sourceSort} dir={sourceDir} onSort={sortSourceTable} />
                  <ServerSortHead label="Status" sortKey="status" active={sourceSort} dir={sourceDir} onSort={sortSourceTable} />
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fundingSources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">
                      {sourcesQ.isLoading
                        ? "Loading capital sources…"
                        : "No capital sources registered yet. Add one to get started."}
                    </TableCell>
                  </TableRow>
                )}
                {shownSources.map((s) => {
                  const inFlight = pendingKinds.get(s.capitalSourceId) ?? [];
                  // Debt has three governable facts, Equity two (no lender, so no advance
                  // rate). The Adjust button only disappears when every one of them is already
                  // in flight — otherwise there is still something to propose.
                  const governableFacts = s.capitalType === "Debt" ? 3 : 2;
                  const noLimits = s.status === "LIVE" && s.limitCount === 0;
                  return (
                    <TableRow key={s.capitalSourceId}>
                      <TableCell>{investorLabel(s.investor)}</TableCell>
                      {/* Blank for equity — there is no book to name, and an em dash says that
                          more honestly than a repeated "Internal" would. */}
                      <TableCell className="text-muted-foreground">
                        {s.facilityName ?? s.facilityCode ?? "—"}
                      </TableCell>
                      <TableCell>{s.capitalType}</TableCell>
                      <TableCell className="text-right font-medium">
                        {usd(s.ceilingDollars)}
                      </TableCell>
                      {/* An em dash means NO RATE RECORDED, not 100%. Rendering an unset rate as
                          100% would assert a haircut nobody agreed to, and every capacity figure
                          derived from it later would inherit the assertion. */}
                      <TableCell className="text-right text-muted-foreground">
                        {ratePct(s.advanceRatePct)}
                      </TableCell>
                      <TableCell>
                        <MappedCodes codes={s.investorCodes} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{s.effectiveStartDate}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] uppercase tracking-[0.1em]",
                              s.status === "LIVE"
                                ? "border-[color:var(--approve)]/50 text-[color:var(--approve)]"
                                : "border-muted-foreground/40 text-muted-foreground",
                            )}
                          >
                            {s.status === "LIVE" ? "Live" : "Superseded"}
                          </Badge>
                          {/* Persists until the source's first limit exists — the durable half
                              of the auto-trigger, and clickable to the same deep link the
                              original prompt offered. */}
                          {noLimits && (
                            <Link
                              to={limitsDeepLink(s)}
                              className="inline-flex items-center gap-1 rounded border border-amber/50 bg-amber-dim/30 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.1em] text-amber transition-colors hover:bg-amber-dim/60"
                              title="This capital source has no limits configured. Nothing is constraining how it is deployed."
                            >
                              <AlertTriangle className="h-3 w-3" />
                              No Limits Set
                            </Link>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {/* Names the fact, not just the fact that something is pending. With
                              three independently-approvable properties, "Pending" alone leaves
                              you unable to tell whether the ceiling or the mapping is the one
                              awaiting review. */}
                          {inFlight.map((k) => (
                            <Badge
                              key={k}
                              variant="outline"
                              className="border-primary/50 text-[10px] uppercase tracking-[0.1em] text-primary"
                              title={`A ${CHANGE_KIND_LABEL[k].toLowerCase()} change is awaiting review.`}
                            >
                              {CHANGE_KIND_LABEL[k]} pending
                            </Badge>
                          ))}
                          {s.status === "LIVE" && inFlight.length < governableFacts && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                setEditSource(s);
                                setDialogOpen(true);
                              }}
                            >
                              Adjust
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Segments 2-4: direct-write settings, no proposal queues (the underlying tables
          have none — see eligibility.py/discount_settings.py/zero_out_rules.py). Replaces
          the Internal caps block that used to live here. */}
      <Card className="mt-6">
        <CardHeader>
          <SegmentHeader
            title="Contract Eligibility"
            description={
              "The gate, applied before any valuation runs. A contract that matches here " +
              "isn't part of this code's pool at all — not \"worth zero,\" simply not " +
              "counted, for any method. Every raw investor code always carries exactly " +
              "these 9 conditions, adjustable per code."
            }
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  {(
                    [
                      ["Investor Code", "investorCode"],
                      ["Facility", "facility"],
                      // Sorts by the condition's rank (Bankruptcy first ... Residual Type last), not alphabetically.
                      ["Condition", "condition"],
                      ["Type", "type"],
                      ["Selection", "selection"],
                      ["Status", "status"],
                    ] as const
                  ).map(([label, key]) => (
                    <ServerSortHead
                      key={key}
                      label={label}
                      sortKey={key}
                      active={eligibilitySort}
                      dir={eligibilityDir}
                      onSort={sortEligibility}
                    />
                  ))}
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eligibilityTotal === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                      {eligibilityQ.isLoading ? "Loading…" : "No conditions configured yet."}
                    </TableCell>
                  </TableRow>
                )}
                {eligibilityPage.map((c) => (
                  <TableRow key={c.eligibilityId}>
                    <TableCell className="font-mono tabular-nums">{c.bankdwnInvestorCode}</TableCell>
                    <TableCell className="text-muted-foreground">{c.facilityLabel ?? "—"}</TableCell>
                    <TableCell>{c.condition}</TableCell>
                    <TableCell className="text-muted-foreground">{c.conditionType}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground">
                      {eligibilitySelectionSummary(c)}
                    </TableCell>
                    <TableCell>
                      <StatusPill on={c.status === "ON"} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          setEditEligibility(c);
                          setEligibilityDialogOpen(true);
                        }}
                      >
                        Adjust
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
            <span>
              {eligibilityTotal === 0
                ? "0 of 0"
                : `${eligibilityOffset + 1}–${Math.min(eligibilityOffset + SEGMENT_PAGE_SIZE, eligibilityTotal)} of ${eligibilityTotal}`}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={eligibilityOffset === 0}
                onClick={() => setEligibilityOffset((o) => Math.max(0, o - SEGMENT_PAGE_SIZE))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={eligibilityOffset + SEGMENT_PAGE_SIZE >= eligibilityTotal}
                onClick={() => setEligibilityOffset((o) => o + SEGMENT_PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <SegmentHeader
            title="Discount & Spread Settings"
            description={
              "What rate Discounted Contract Balance discounts at, and Fair Value's spread " +
              "matrix (grade × industry × term × curve) — both per facility. Net " +
              "Investment needs nothing here."
            }
            right={
              <>
                <span className="relative inline-flex">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setMatrixDialogOpen(true)}
                  >
                    Spread Matrix
                  </Button>
                  <CountBadge count={pendingFvBatchCount} />
                </span>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditDiscountSetting(null);
                    setDiscountDialogOpen(true);
                  }}
                >
                  Configure
                </Button>
              </>
            }
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  {(
                    [
                      ["Investor Code", "investorCode"],
                      ["Facility", "facility"],
                      ["DCB Discount Rate", "dcbRate"],
                      ["FMV Spread Matrix", "fmvSpread"],
                    ] as const
                  ).map(([label, key]) => (
                    <ServerSortHead
                      key={key}
                      label={label}
                      sortKey={key}
                      active={discountSort}
                      dir={discountDir}
                      onSort={sortDiscount}
                    />
                  ))}
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discountTotal === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                      {discountSettingsQ.isLoading ? "Loading…" : "No settings configured yet."}
                    </TableCell>
                  </TableRow>
                )}
                {discountPage.map((s) => (
                  <TableRow key={s.settingId}>
                    <TableCell className="font-mono tabular-nums">{s.bankdwnInvestorCode}</TableCell>
                    <TableCell className="text-muted-foreground">{s.facilityLabel ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {dcbRateSummary(s)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{fmvSummaryLabel(s)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          setEditDiscountSetting(s);
                          setDiscountDialogOpen(true);
                        }}
                      >
                        Adjust
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
            <span>
              {discountTotal === 0
                ? "0 of 0"
                : `${discountOffset + 1}–${Math.min(discountOffset + SEGMENT_PAGE_SIZE, discountTotal)} of ${discountTotal}`}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={discountOffset === 0}
                onClick={() => setDiscountOffset((o) => Math.max(0, o - SEGMENT_PAGE_SIZE))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={discountOffset + SEGMENT_PAGE_SIZE >= discountTotal}
                onClick={() => setDiscountOffset((o) => o + SEGMENT_PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <SegmentHeader
            title="Zero Out Rules"
            description={
              "Given a contract already passed Eligibility, does its current status zero " +
              "out its value under this specific method. Every raw investor code always " +
              "carries exactly these 4 rules (DCB/FMV × Default/Delinquent) — day " +
              "count and % of payments, adjustable per code."
            }
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  {(
                    [
                      ["Investor Code", "investorCode", ""],
                      ["Facility", "facility", ""],
                      ["Method", "method", ""],
                      ["Condition", "condition", ""],
                      ["Day Count", "dayCount", "text-right"],
                      ["% of Payments", "pctOfPayments", "text-right"],
                      ["Status", "status", ""],
                    ] as const
                  ).map(([label, key, align]) => (
                    <ServerSortHead
                      key={key}
                      label={label}
                      sortKey={key}
                      active={zeroOutSort}
                      dir={zeroOutDir}
                      onSort={sortZeroOut}
                      className={align}
                    />
                  ))}
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zeroOutTotal === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                      {zeroOutQ.isLoading ? "Loading…" : "No rules configured yet."}
                    </TableCell>
                  </TableRow>
                )}
                {zeroOutPage.map((r) => (
                  <TableRow key={r.ruleId}>
                    <TableCell className="font-mono tabular-nums">{r.bankdwnInvestorCode}</TableCell>
                    <TableCell className="text-muted-foreground">{r.facilityLabel ?? "—"}</TableCell>
                    <TableCell>{r.method}</TableCell>
                    <TableCell>{r.condition}</TableCell>
                    <TableCell className="text-right">
                      {r.dayCount !== null ? `${r.dayCount} days` : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.pctOfPayments !== null ? `${r.pctOfPayments}%` : "—"}
                    </TableCell>
                    <TableCell>
                      <StatusPill on={r.status === "ON"} offLabel={r.offReason ?? undefined} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          setEditZeroOutRule(r);
                          setZeroOutDialogOpen(true);
                        }}
                      >
                        Adjust
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
            <span>
              {zeroOutTotal === 0
                ? "0 of 0"
                : `${zeroOutOffset + 1}–${Math.min(zeroOutOffset + SEGMENT_PAGE_SIZE, zeroOutTotal)} of ${zeroOutTotal}`}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={zeroOutOffset === 0}
                onClick={() => setZeroOutOffset((o) => Math.max(0, o - SEGMENT_PAGE_SIZE))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={zeroOutOffset + SEGMENT_PAGE_SIZE >= zeroOutTotal}
                onClick={() => setZeroOutOffset((o) => o + SEGMENT_PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AddCapitalSourceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        sources={sources}
        editSource={editSource}
        propose={(input) => propose.mutateAsync(input)}
        onSubmitted={(m) => setToast(m)}
        onReviewApplied={(res) => {
          if (res.needsLimits) setPrompt(res);
        }}
      />
      <EligibilityConditionDialog
        open={eligibilityDialogOpen}
        onOpenChange={setEligibilityDialogOpen}
        condition={editEligibility}
        onSubmitted={(m) => setToast(m)}
      />
      <DiscountSpreadSettingsDialog
        open={discountDialogOpen}
        onOpenChange={setDiscountDialogOpen}
        settings={allDiscountSettingsQ.data?.settings ?? []}
        setting={editDiscountSetting}
        onSubmitted={(m) => setToast(m)}
      />
      <Dialog open={matrixDialogOpen} onOpenChange={setMatrixDialogOpen}>
        <PrcDialogContent className="sm:max-w-3xl">
          <ContractValuationSettings />
        </PrcDialogContent>
      </Dialog>
      <ZeroOutRuleDialog
        open={zeroOutDialogOpen}
        onOpenChange={setZeroOutDialogOpen}
        rule={editZeroOutRule}
        onSubmitted={(m) => setToast(m)}
      />
    </ManageLimitsShell>
  );
}

/** A read-only ON/OFF status pill, matching the LIVE/Superseded badge convention already on
 *  this page — Segments 2 and 4 have no interactive switch in the table itself, only inside
 *  their Adjust dialogs (see ContractEligibility/ZeroOutRules API modules' docstrings). */
function StatusPill({ on, offLabel }: { on: boolean; offLabel?: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-[10px] uppercase tracking-[0.1em]",
        on
          ? "border-[color:var(--approve)]/50 text-[color:var(--approve)]"
          : "border-muted-foreground/40 text-muted-foreground",
      )}
      title={!on && offLabel ? offLabel : undefined}
    >
      {on ? "On" : offLabel ? `Off · ${offLabel}` : "Off"}
    </Badge>
  );
}

function promptLabel(r: CapitalReviewResult): string {
  return r.capitalType === "Equity"
    ? `${INVESTOR_LABEL[(r.investor ?? "") as "WAFRA"] ?? r.investor} equity`
    : (r.facilityName ?? r.facilityCode ?? "This facility");
}

/**
 * The mapped codes cell, and the three states it has to tell apart.
 *
 * null is an OMISSION — nobody has said which codes are this pool — and [] is a DECISION
 * someone proposed and a reviewer approved. Collapsing them into one em dash would hide the
 * only one of the two that needs chasing.
 */
function MappedCodes({ codes }: { codes: string[] | null }) {
  if (codes === null) {
    return (
      <span
        className="text-xs text-muted-foreground"
        title="No code mapping has been set for this source. Nothing records which contracts constitute it."
      >
        Not Mapped
      </span>
    );
  }
  if (codes.length === 0) {
    return (
      <span
        className="text-xs text-muted-foreground"
        title="An approved mapping names no codes — this pool is explicitly empty."
      >
        None
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {codes.map((c) => (
        <Badge
          key={c}
          variant="outline"
          className="border-border px-1.5 py-0 font-mono text-[10px] tabular-nums"
        >
          {c}
        </Badge>
      ))}
    </div>
  );
}

